import { put, putResolve, select, takeLatest } from 'typed-redux-saga/macro';
import { getBanks } from 'actions/linkBank';
import { SET_CONNECTIONS_STATUS } from 'actions/types';
import { REDEEM_EMPLOYER_BENEFIT_SUCCESS } from 'actions/types/organisations';
import { IAP_PURCHASE_FLOW_ENDED_WITH_SUCCESS } from 'features/premium/actions/iapSubscriptions';
import { REDEEM_STRIPE_GIFT_SUCCESS } from 'features/premium/actions/types';
import { Tier, tierGreaterOrEqualThan } from 'features/premium/entities';
import { selectActiveTier } from 'features/premium/selectors';
import { getCreditAlerts, getCreditReport, getCreditScore, getCreditScoreHistory, getCreditScoreRegistrationStatus, getEnhancedFactors, resetCreditScoreData, } from '../actions';
import { CREDIT_SCORE_GET_REGISTRATION_STATUS_SUCCESS, CREDIT_SCORE_GET_REPORT_REQUEST, CREDIT_SCORE_REQUEST_FETCH_FLOW, } from '../actions/types';
import { selectCreditReportParams, selectHasCreditReport, selectIsCreditScoreAlertsAvailable, selectIsCreditScoreAvailable, selectIsCreditScoreReportAvailable, selectPendingConnectionRefetch, selectShouldShowCreditScoreHistory, selectShouldShowSuggestedConnections, } from '../selectors';
const PLAN_CHANGE_TRIGGERS = [
    'SUBSCRIBE_VIA_STRIPE_SUCCESS',
    'CHANGE_SUBSCRIPTION_VIA_STRIPE_SUCCESS',
    'CANCEL_STRIPE_SUBSCRIPTION_SUCCESS',
    'REACTIVATE_STRIPE_SUBSCRIPTION_SUCCESS',
    'REFUND_STRIPE_SUBSCRIPTION_SUCCESS',
    IAP_PURCHASE_FLOW_ENDED_WITH_SUCCESS,
    REDEEM_STRIPE_GIFT_SUCCESS,
    REDEEM_EMPLOYER_BENEFIT_SUCCESS,
];
const isFetchTrigger = (action) => action.type === CREDIT_SCORE_REQUEST_FETCH_FLOW ||
    PLAN_CHANGE_TRIGGERS.includes(action.type);
function* fetchCreditScoreFlow(action) {
    const isAvailable = yield* select(selectIsCreditScoreAvailable);
    if (!isAvailable)
        return;
    const result = yield* putResolve(getCreditScoreRegistrationStatus());
    if (result?.type !== CREDIT_SCORE_GET_REGISTRATION_STATUS_SUCCESS)
        return;
    const tier = yield* select(selectActiveTier);
    if (result.payload.status !== 'REGISTERED' ||
        !tierGreaterOrEqualThan(tier, Tier.plus)) {
        yield* put(resetCreditScoreData());
        return;
    }
    const isAlertsAvailable = yield* select(selectIsCreditScoreAlertsAvailable);
    if (isAlertsAvailable) {
        yield* put(getCreditAlerts());
    }
    // Await /score before the other endpoints: the backend caches the report it
    // computes for /score, so /factors, /history and /report below return from
    // the warm cache. skipScore means the caller already awaited /score itself.
    // A failed /score still falls through — the other fetches are just slower.
    if (action.payload?.skipScore !== true) {
        yield* putResolve(getCreditScore());
    }
    yield* put(getEnhancedFactors());
    const isPlanChange = PLAN_CHANGE_TRIGGERS.includes(action.type);
    const includeHistory = action.payload?.includeHistory === true;
    if (isPlanChange || includeHistory) {
        const shouldShowHistory = yield* select(selectShouldShowCreditScoreHistory);
        if (shouldShowHistory) {
            yield* put(getCreditScoreHistory());
        }
    }
    const includeReport = action.payload?.includeReport === true;
    if (!isPlanChange && !includeReport)
        return;
    const isReportAvailable = yield* select(selectIsCreditScoreReportAvailable);
    if (isReportAvailable) {
        const params = yield* select(selectCreditReportParams);
        yield* put(getCreditReport(params));
    }
}
function* reconcileSuggestedConnections(action) {
    if (action.payload !== false)
        return;
    const shouldReconcile = yield* select(selectShouldShowSuggestedConnections);
    if (!shouldReconcile)
        return;
    const hasReport = yield* select(selectHasCreditReport);
    const pendingRefetch = yield* select(selectPendingConnectionRefetch);
    if (!hasReport || !pendingRefetch)
        return;
    const params = yield* select(selectCreditReportParams);
    yield* put(getCreditReport({
        ...params,
        includeConnectionStatus: true,
        isConnectionReconcile: true,
    }));
}
// Suggested connections come from the report, but resolving each one to a bank
// needs the bank catalogue (state.linkBank.banks), which is not persisted. Load
// it alongside the report fetch — only when the feature is on and the catalogue
// is neither present nor already being fetched — so the suggestion UI can
// resolve without firing duplicate getBanks. isFetching flips to true on
// BANKS_REQUEST (dispatched synchronously by the api middleware), so a second
// report request while the first /banks call is in flight is a no-op.
function* ensureBanksForSuggestions() {
    const shouldShow = yield* select(selectShouldShowSuggestedConnections);
    if (!shouldShow)
        return;
    const { banks, isFetching } = yield* select((state) => state.linkBank);
    if (banks.length > 0 || isFetching)
        return;
    yield* put(getBanks());
}
function* creditScoreSaga() {
    yield* takeLatest(isFetchTrigger, fetchCreditScoreFlow);
    yield* takeLatest(SET_CONNECTIONS_STATUS, reconcileSuggestedConnections);
    yield* takeLatest(CREDIT_SCORE_GET_REPORT_REQUEST, ensureBanksForSuggestions);
}
export default creditScoreSaga;
